import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';
import AdminPageHeader from './AdminPageHeader';
import EditModal from './EditModal';
import Pagination from './Pagination';
import { FaUserPlus, FaEdit, FaTrash, FaSave, FaTimes, FaUserFriends, FaInstagram, FaExternalLinkAlt, FaSearch, FaFilter } from 'react-icons/fa';

const AdminInfluencer = () => {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    instagram: '',
    influencerType: 'Normal',
    status: 'approved',
    influencerName: '',
    bio: '',
    category: 'Other',
    instaSubs: '',
    facebook: '',
    twitter: '',
    price: 0,
    engagementPercentage: 0,
    averageViews: 0,
    clients: '',
    awards: '',
    influencerImage: null,
    password: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.INFLUENCER);
      setInfluencers(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching influencers:', error);
      setError('Failed to fetch influencers');
    } finally {
      setLoading(false);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      instagram: '',
      influencerType: 'Normal',
      status: 'approved',
      influencerName: '',
      bio: '',
      category: 'Other',
      instaSubs: '',
      facebook: '',
      twitter: '',
      price: 0,
      engagementPercentage: 0,
      averageViews: 0,
      clients: '',
      awards: '',
      influencerImage: null,
      password: ''
    });
    setEditingInfluencer(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Basic fields
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('instagram', formData.instagram);
      submitData.append('influencerType', formData.influencerType);
      submitData.append('status', 'approved'); // Auto-approve when created by admin
      submitData.append('transactionId', `ADMIN_${Date.now()}`); // Admin transaction ID
      
      // Profile fields
      submitData.append('influencerName', formData.influencerName || formData.name);
      submitData.append('bio', formData.bio);
      submitData.append('category', formData.category);
      submitData.append('instaSubs', formData.instaSubs);
      submitData.append('facebook', formData.facebook);
      submitData.append('twitter', formData.twitter);
      submitData.append('price', formData.price || 0);
      submitData.append('engagementPercentage', formData.engagementPercentage || 0);
      submitData.append('averageViews', formData.averageViews || 0);
      
      // Handle arrays
      if (formData.clients) {
        submitData.append('clients', formData.clients.split(',').map(c => c.trim()).join(','));
      }
      if (formData.awards) {
        submitData.append('awards', formData.awards.split(',').map(a => a.trim()).join(','));
      }
      
      // Password (optional for admin-created influencers)
      if (formData.password) {
        submitData.append('password', formData.password);
      }
      
      // Image
      if (formData.influencerImage) {
        submitData.append('influencerImage', formData.influencerImage);
      }

      if (editingInfluencer) {
        // Update existing influencer via PUT route
        const response = await axios.put(`${API_ENDPOINTS.INFLUENCER}/${editingInfluencer._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchInfluencers();
        setShowModal(false);
        resetForm();
      } else {
        // Create new influencer via admin route
        const response = await axios.post(`${API_ENDPOINTS.INFLUENCER}/admin/create`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await fetchInfluencers();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error saving influencer:', error);
      setError(error.response?.data?.error || error.message || 'Failed to save influencer');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (influencer) => {
    setEditingInfluencer(influencer);
    setFormData({
      name: influencer.name || '',
      phone: influencer.phone || '',
      email: influencer.email?.includes('no-email.internal') ? '' : influencer.email || '',
      instagram: influencer.instagram || '',
      influencerType: influencer.influencerType || 'Normal',
      status: influencer.status || 'approved',
      influencerName: influencer.influencerName || influencer.name || '',
      bio: influencer.bio || '',
      category: influencer.category || 'Other',
      instaSubs: influencer.instaSubs || '',
      facebook: influencer.facebook || '',
      twitter: influencer.twitter || '',
      price: influencer.price || 0,
      engagementPercentage: influencer.engagementPercentage || 0,
      averageViews: influencer.averageViews || 0,
      clients: Array.isArray(influencer.clients) ? influencer.clients.join(', ') : (influencer.clients || ''),
      awards: Array.isArray(influencer.awards) ? influencer.awards.join(', ') : (influencer.awards || ''),
      influencerImage: null,
      password: ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this influencer?')) return;
    
    try {
      await axios.delete(`${API_ENDPOINTS.INFLUENCER}/${id}`);
      await fetchInfluencers();
    } catch (error) {
      console.error('Error deleting influencer:', error);
    }
  };

  const filteredInfluencers = influencers.filter(i => {
    const matchesSearch = 
      !search ||
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.email?.toLowerCase().includes(search.toLowerCase()) ||
      i.phone?.toLowerCase().includes(search.toLowerCase()) ||
      i.instagram?.toLowerCase().includes(search.toLowerCase()) ||
      i.influencerName?.toLowerCase().includes(search.toLowerCase());
      
    const matchesType = typeFilter === 'all' || i.influencerType === typeFilter;
    const matchesCategory = categoryFilter === 'all' || i.category === categoryFilter;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  // Reset page on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, categoryFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInfluencers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInfluencers = filteredInfluencers.slice(startIndex, endIndex);

  const handlePageChange = (page, newItemsPerPage = itemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    }
  };

  if (loading && influencers.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading influencers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <AdminPageHeader
        title="Manage Influencers"
        subtitle="Add and manage normal and featured influencers directly"
        icon={<FaUserFriends className="w-6 h-6" />}
      >
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 shadow-md"
        >
          <FaUserPlus /> Add New Influencer
        </button>
      </AdminPageHeader>

      <div className="pt-32 p-6 max-w-7xl mx-auto">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4"
          >
            <div className="text-red-600 font-semibold">{error}</div>
          </motion.div>
        )}

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {[
            { label: 'Total Creators', val: influencers.length, color: 'text-slate-800', bg: 'bg-white border-slate-100' },
            { label: 'Active Roster', val: influencers.filter(i => i.status === 'approved').length, color: 'text-emerald-600', bg: 'bg-white border-emerald-100' },
            { label: 'Featured Tier', val: influencers.filter(i => i.influencerType === 'Featured Influencer').length, color: 'text-amber-600', bg: 'bg-white border-amber-100' },
            { label: 'Standard Tier', val: influencers.filter(i => i.influencerType === 'Normal').length, color: 'text-blue-600', bg: 'bg-white border-blue-100' }
          ].map((stat, i) => (
            <div key={i} className={`border rounded-2xl p-5 shadow-sm hover:shadow transition-shadow ${stat.bg}`}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.val}</div>
            </div>
          ))}
        </motion.div>

        {/* Unified Search & Control Bar */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-4 items-center justify-between mb-6"
        >
          {/* Search Input */}
          <div className="relative w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-3.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, phone or handle..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition placeholder:text-slate-400"
            />
          </div>

          {/* Filters Area */}
          <div className="flex flex-wrap md:flex-nowrap gap-3 items-center w-full md:w-auto">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap">
              <FaFilter className="w-3.5 h-3.5 text-slate-400" />
              Filter Board:
            </span>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition bg-white"
            >
              <option value="all">All Tiers</option>
              <option value="Normal">Standard</option>
              <option value="Featured Influencer">Featured</option>
            </select>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition bg-white"
            >
              <option value="all">All Categories</option>
              {['Food', 'Travel', 'Finance', 'Fitness', 'Fashion', 'Gaming', 'Tech', 'Education', 'Entertainment', 'Other'].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Influencers Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Influencer Profile</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Instagram</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Tier & Category</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Rates & Performance</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right font-semibold text-slate-500 text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredInfluencers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16 text-slate-500">
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                          <FaUserFriends className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-base font-semibold text-slate-700">No creators found</p>
                        <p className="text-xs text-slate-400 mt-1">Try adjusting your filters or search keywords</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedInfluencers.map((influencer) => (
                    <tr key={influencer._id} className="hover:bg-slate-50/40 transition-colors">
                      {/* Influencer Profile */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          {influencer.influencerImage ? (
                            <img
                              src={getImageUrl(influencer.influencerImage)}
                              alt={influencer.name}
                              className="w-11 h-11 rounded-full object-cover border border-slate-100 shadow-sm"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cbd5e1'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
                              }}
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                              <FaUserFriends className="text-slate-400 w-5 h-5" />
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{influencer.name}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              {influencer.email?.includes('no-email.internal') ? 'No Email' : influencer.email}
                            </div>
                            {influencer.phone && (
                              <div className="text-[10px] text-slate-400 font-medium font-mono mt-0.5">{influencer.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Instagram */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {influencer.instagram ? (
                          <a 
                            href={influencer.instagram} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-pink-200/50 text-pink-700 rounded-full text-xs font-semibold transition-all duration-200"
                          >
                            <FaInstagram className="w-3.5 h-3.5 text-pink-600" />
                            <span>Profile Link</span>
                            <FaExternalLinkAlt className="w-2.5 h-2.5 opacity-65" />
                          </a>
                        ) : (
                          <span className="text-slate-400 text-xs italic">N/A</span>
                        )}
                      </td>

                      {/* Tier & Category */}
                      <td className="px-6 py-4 whitespace-nowrap space-y-1">
                        <div>
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold ${
                              influencer.influencerType === 'Featured Influencer'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200/40'
                                : 'bg-slate-50 text-slate-600 border border-slate-200/40'
                            }`}
                          >
                            {influencer.influencerType}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 pl-0.5 font-medium">
                          Category: <span className="text-slate-700">{influencer.category || 'Other'}</span>
                        </div>
                      </td>

                      {/* Rates & Performance */}
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 space-y-0.5 font-medium">
                        <div>
                          Price: <span className="text-slate-800 font-bold font-mono">₹{influencer.price?.toLocaleString('en-IN') || 0}</span>
                        </div>
                        <div className="text-[11px] text-slate-400">
                          Avg Views: <span className="text-slate-600 font-semibold">{influencer.averageViews ? influencer.averageViews.toLocaleString() : 'N/A'}</span>
                        </div>
                        <div className="text-[11px] text-slate-400">
                          Engagement: <span className="text-slate-600 font-semibold">{influencer.engagementPercentage || 0}%</span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border ${
                            influencer.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : influencer.status === 'pending'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}
                        >
                          {influencer.status.toUpperCase()}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleEdit(influencer)}
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-transparent hover:border-slate-200 transition-all"
                            title="Edit Creator Profile"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(influencer._id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-100 transition-all"
                            title="Remove Creator"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={filteredInfluencers.length}
          showItemsPerPage={true}
          itemsPerPageOptions={[5, 10, 20, 50]}
        />

        {/* Add/Edit Modal */}
        {showModal && (
          <EditModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              resetForm();
            }}
            title={editingInfluencer ? 'Edit Influencer' : 'Add New Influencer'}
            onSave={handleSubmit}
            saveLabel={editingInfluencer ? 'Update' : 'Create'}
            isLoading={loading}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram *</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">X (Twitter)</label>
                  <input
                    type="text"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Influencer Type *</label>
                  <select
                    name="influencerType"
                    value={formData.influencerType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none bg-white"
                  >
                    <option value="Normal">Normal</option>
                    <option value="Featured Influencer">Featured Influencer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none bg-white"
                  >
                    <option value="Food">Food</option>
                    <option value="Travel">Travel</option>
                    <option value="Finance">Finance</option>
                    <option value="Fitness">Fitness</option>
                    <option value="Fashion">Fashion</option>
                    <option value="Gaming">Gaming</option>
                    <option value="Tech">Tech</option>
                    <option value="Education">Education</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Influencer Display Name</label>
                  <input
                    type="text"
                    name="influencerName"
                    value={formData.influencerName}
                    onChange={handleInputChange}
                    placeholder="Leave empty to use name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Followers</label>
                  <input
                    type="text"
                    name="instaSubs"
                    value={formData.instaSubs}
                    onChange={handleInputChange}
                    placeholder="e.g., 1.2M, 500K"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Engagement Percentage</label>
                  <input
                    type="number"
                    step="0.1"
                    name="engagementPercentage"
                    value={formData.engagementPercentage}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Average Views (in thousands)</label>
                  <input
                    type="number"
                    name="averageViews"
                    value={formData.averageViews}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clients (comma separated)</label>
                  <input
                    type="text"
                    name="clients"
                    value={formData.clients}
                    onChange={handleInputChange}
                    placeholder="e.g., Nike, Adidas, Apple"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Awards (comma separated)</label>
                  <input
                    type="text"
                    name="awards"
                    value={formData.awards}
                    onChange={handleInputChange}
                    placeholder="e.g., Best Influencer 2023, Award Winner"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                  <input
                    type="file"
                    name="influencerImage"
                    accept="image/*"
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-orange-100 file:via-pink-100 file:to-purple-100 file:text-orange-600"
                  />
                </div>
                {!editingInfluencer && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password (Optional)</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Leave empty for now"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none"
                    />
                  </div>
                )}
              </div>
            </form>
          </EditModal>
        )}
      </div>
    </div>
  );
};

export default AdminInfluencer;

